import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Modal, Tabs, Tab, FooterButton } from 'Core/components';
import { AppModal } from '../components/app-modals';
import { addChangeRequest, editChangeRequest } from './store/slice';
import { selectChangeRequestSaveLoading } from './store/selectors';
import { showToast } from 'Core/redux/toast/slice';
import Select, { createFilter } from 'react-select';
import { ChangeRequestService, CR_ASSIGABLE_USERS_FALLBACK } from '../../service/api/change-request-service';

/* ──────────────────────────────────────────────────────────────────────────────
   Responsibilities (roles) – not loaded from API
   ──────────────────────────────────────────────────────────────────────────── */
const RESPONSIBILITY_OPTIONS = [
  'Project Manager', 'Quality Engineer', 'Process Engineer', 'Operations Manager',
  'IT Lead', 'Finance Controller', 'Supply Chain Manager', 'HSE Officer',
  'Production Supervisor', 'Maintenance Technician',
];

const statusBadgeColor = status =>
  status === 'Approved' ? '#28a745'
    : status === 'Pending' ? '#ffc107'
    : status === 'In Review' ? '#17a2b8'
    : status === 'Cancelled' ? '#6c757d'
    : '#dc3545';

function crNorm(s) {
  return (s == null ? '' : String(s)).trim().toLowerCase();
}

/** Strings to match signed-in user (Entra display name vs local session). */
function collectCurrentUserIdentityStrings() {
  const out = [];
  if (typeof localStorage === 'undefined') return out;
  const push = v => {
    const t = crNorm(v);
    if (t) out.push(t);
  };
  push(localStorage.getItem('UserName'));
  push(localStorage.getItem('UserEmail'));
  try {
    const li = JSON.parse(localStorage.getItem('LoginInfo') || '{}');
    const u = li?.user;
    if (u?.firstName || u?.lastName) {
      push(`${u.firstName || ''} ${u.lastName || ''}`.trim());
    }
  } catch (_e) {
    /* ignore */
  }
  return out;
}

/** True when this row's responsible person is the current user (by name / email). */
function responsiblePersonIsCurrentUser(responsiblePerson) {
  const r = crNorm(responsiblePerson);
  if (!r) return false;
  const ids = collectCurrentUserIdentityStrings();
  if (ids.length === 0) return false;
  for (const t of ids) {
    if (r === t) return true;
  }
  for (const t of ids) {
    if (t.length >= 3 && (r.includes(t) || t.includes(r))) return true;
  }
  return false;
}

/** Portal react-select menus above Bootstrap modal stacking (modal ~1055). Hover/selected colors: global SCSS components/_react-select.scss ($primary). */
const CR_MODAL_SELECT_Z = 1080;
const crModalSelectProps = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed',
};
const crModalSelectStyles = extra => ({
  ...(extra || {}),
  menuPortal: base => ({ ...base, zIndex: CR_MODAL_SELECT_Z }),
});

/** Type-to-filter user lists (display name, job title in label, email-style matches). */
const crAssignableUserFilter = createFilter({
  ignoreAccents: true,
  trim: true,
  stringify: option => `${option.label ?? ''} ${option.value ?? ''}`,
});

/* ──────────────────────────────────────────────────────────────────────────────
   Responsibilities Dropdown – assignable users from User/entra-users (Entra)
   ──────────────────────────────────────────────────────────────────────────── */
const ResponsibilitiesDropdown = ({ value, onChange, assignableUsers = CR_ASSIGABLE_USERS_FALLBACK }) => {
  const list = assignableUsers?.length ? assignableUsers : CR_ASSIGABLE_USERS_FALLBACK;
  const options = list.map(u => ({
    value: u.displayName,
    label: u.departmentName ? `${u.displayName} — ${u.departmentName}` : u.displayName,
  }));
  return (
    <Select
      {...crModalSelectProps}
      menuPlacement="auto"
      options={options}
      value={
        value
          ? options.find(o => o.value === value) || { value, label: value }
          : null
      }
      onChange={o => onChange(o?.value ?? '')}
      placeholder="Type to search users..."
      isSearchable
      isClearable
      filterOption={crAssignableUserFilter}
      classNamePrefix="react-select"
      styles={crModalSelectStyles({
        control: base => ({ ...base, minHeight: '32px', fontSize: '0.84rem' }),
      })}
    />
  );
};

/* ──────────────────────────────────────────────────────────────────────────────
   Tab 1 – Details
   ──────────────────────────────────────────────────────────────────────────── */
const resourcePlanRowHasContent = row =>
  Boolean(
    (row?.category || '').trim()
      || (row?.description || '').trim()
      || (row?.estimatedCost || '').trim()
      || (row?.assignedTo || '').trim(),
  );

const DetailsTab = ({ data, onChange, masterRisks = [], masterOpportunities = [], resourceDetails = [] }) => {
  const [purpose, setPurpose] = useState(data?.purpose || '');
  const [qmsIntegrity, setQmsIntegrity] = useState(data?.qmsIntegrity || '');

  const planRowsForDisplay = (resourceDetails || []).filter(resourcePlanRowHasContent);
  /** Category (resource type) first; else description as the row’s resource name. */
  const resourcePlanNameForRow = row => {
    const cat = (row?.category || '').trim();
    if (cat) return cat;
    return (row?.description || '').trim();
  };
  const resourcePlanDisplayNames = [
    ...new Set(planRowsForDisplay.map(resourcePlanNameForRow).filter(Boolean)),
  ];

  /* Responsibilities – role dropdown + name */
  const [responsibilities, setResponsibilities] = useState(() => {
    if (Array.isArray(data?.responsibilities) && data.responsibilities.length > 0)
      return data.responsibilities.map((r, i) => ({
        id: i + 1,
        role: typeof r === 'string' ? r.split(':')[0]?.trim() : r.role || '',
        name: typeof r === 'string' && r.includes(':') ? r.split(':').slice(1).join(':').trim() : r.name || '',
      }));
    return [{ id: 1, role: 'Project Manager', name: '' }];
  });

  /* Risks – type + description dropdown */
  const [risks, setRisks] = useState(() => {
    if (Array.isArray(data?.risks) && data.risks.length > 0)
      return data.risks.map((r, i) => ({
        id: i + 1,
        type: r.type || 'Risk',
        description: r.description || (typeof r === 'string' ? r.split(':').slice(1).join(':').trim() : ''),
      }));
    return [];
  });

  const notify = patch => onChange && onChange(patch);

  /* Responsibility helpers */
  const addResponsibility = () => {
    const u = [...responsibilities, { id: Date.now(), role: '', name: '' }];
    setResponsibilities(u); notify({ responsibilities: u.map(r => `${r.role}: ${r.name}`) });
  };
  const removeResponsibility = id => {
    const u = responsibilities.filter(r => r.id !== id);
    setResponsibilities(u); notify({ responsibilities: u.map(r => `${r.role}: ${r.name}`) });
  };
  const updateResponsibility = (id, field, value) => {
    const u = responsibilities.map(r => r.id === id ? { ...r, [field]: value } : r);
    setResponsibilities(u); notify({ responsibilities: u.map(r => `${r.role}: ${r.name}`) });
  };

  /* Risk helpers */
  const addRisk = () => {
    const u = [...risks, { id: Date.now(), type: 'Risk', description: '' }];
    setRisks(u); notify({ risks: u.map(r => ({ type: r.type, description: r.description })) });
  };
  const removeRisk = id => {
    const u = risks.filter(r => r.id !== id);
    setRisks(u); notify({ risks: u.map(r => ({ type: r.type, description: r.description })) });
  };
  const updateRisk = (id, field, value) => {
    const u = risks.map(r => r.id === id ? { ...r, [field]: value } : r);
    setRisks(u); notify({ risks: u.map(r => ({ type: r.type, description: r.description })) });
  };

  const updateRisksByType = (type, selectedOptions) => {
    // Keep internal risks of other types
    const otherTypes = risks.filter(r => r.type !== type);
    const newItems = (selectedOptions || []).map((opt, idx) => ({
      id: `${type}-${Date.now()}-${idx}`,
      type: type,
      description: opt.value,
    }));
    const updated = [...otherTypes, ...newItems];
    setRisks(updated);
    notify({ risks: updated.map(r => ({ type: r.type, description: r.description })) });
  };

  const SectionHeader = ({ label, onAdd }) => (
    <>
      <div className="d-flex justify-content-between align-items-center">
        <span style={{ color: '#e68a00', fontWeight: '600', fontSize: '0.88rem' }}>{label}</span>
        {onAdd && (
          <button
            type="button"
            onClick={onAdd}
            style={{
              fontSize: '0.72rem', color: '#1a56db', border: '1px solid #1a56db',
              background: 'transparent', borderRadius: '4px', padding: '1px 8px', cursor: 'pointer',
            }}
          >
            + Add
          </button>
        )}
      </div>
      <hr style={{ borderColor: '#e68a00', marginTop: '4px', marginBottom: '8px', opacity: 0.35 }} />
    </>
  );

  return (
    <div style={{ fontSize: '0.88rem' }}>

      {/* Purpose & Consequences + QMS Integrity – same row, 50% each */}
      <div className="row g-3 mb-1">
        <div className="col-6">
          <SectionHeader label="Purpose & Consequences" />
          <textarea
            className="form-control form-control-sm"
            rows={3}
            placeholder="Describe the purpose and consequences of this change..."
            value={purpose}
            onChange={e => { setPurpose(e.target.value); notify({ purpose: e.target.value }); }}
          />
        </div>
        <div className="col-6">
          <SectionHeader label="QMS Integrity" />
          <textarea
            className="form-control form-control-sm"
            rows={3}
            placeholder="How does this change affect QMS integrity?"
            value={qmsIntegrity}
            onChange={e => { setQmsIntegrity(e.target.value); notify({ qmsIntegrity: e.target.value }); }}
          />
        </div>
      </div>

      {/* 2-column layout */}
      <div className="row g-3">

        {/* ── LEFT: Resources + Responsibilities ── */}
        <div className="col-lg-6">

          {/* Resources Needed – resource names from Resource Plan (category, else description) */}
          <div className="mb-3">
            <SectionHeader label="Resources Needed" />
            <div
              className="rounded px-3 py-2"
              style={{ border: '1px solid #dee2e6', background: '#fafbff', minHeight: 48 }}
            >
              {planRowsForDisplay.length === 0 ? (
                <span style={{ color: '#aaa', fontSize: '0.82rem' }}>
                  No resources in the plan yet. Add or edit them in the Resources tab.
                </span>
              ) : resourcePlanDisplayNames.length === 0 ? (
                <span style={{ color: '#aaa', fontSize: '0.82rem' }}>
                  Add a category or description for each row in the Resources tab.
                </span>
              ) : (
                <span style={{ fontSize: '0.85rem', color: '#1a56db', fontWeight: 500 }}>
                  {resourcePlanDisplayNames.join(', ')}
                </span>
              )}
            </div>
          </div>

          {/* Risk & Opportunities – Multi-select for Risk and Opportunity */}
          <div className="mb-3">
            <SectionHeader label="Risk & Opportunities" />
            
            {/* Risks */}
            <div className="mb-3">
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#dc3545', marginBottom: '4px', display: 'block' }}>⚠ RISKS</label>
              <Select
                isMulti
                {...crModalSelectProps}
                menuPlacement="auto"
                options={masterRisks.map(opt => ({ value: opt, label: opt }))}
                value={risks.filter(r => r.type === 'Risk').map(r => ({ value: r.description, label: r.description }))}
                onChange={selected => updateRisksByType('Risk', selected)}
                classNamePrefix="react-select"
                placeholder="Select risks..."
                styles={crModalSelectStyles({
                  control: base => ({ ...base, minHeight: '36px', fontSize: '0.83rem', borderRadius: '8px' }),
                })}
              />
              <div className="mt-1 px-2" style={{ fontSize: '0.78rem', color: '#666', fontStyle: 'italic' }}>
                {risks.filter(r => r.type === 'Risk').map(r => r.description).join(', ') || 'No risks selected'}
              </div>
            </div>

            {/* Opportunities */}
            <div className="mb-2">
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#28a745', marginBottom: '4px', display: 'block' }}>✓ OPPORTUNITIES</label>
              <Select
                isMulti
                {...crModalSelectProps}
                menuPlacement="auto"
                options={masterOpportunities.map(opt => ({ value: opt, label: opt }))}
                value={risks.filter(r => r.type === 'Opportunity').map(r => ({ value: r.description, label: r.description }))}
                onChange={selected => updateRisksByType('Opportunity', selected)}
                classNamePrefix="react-select"
                placeholder="Select opportunities..."
                styles={crModalSelectStyles({
                  control: base => ({ ...base, minHeight: '36px', fontSize: '0.83rem', borderRadius: '8px' }),
                })}
              />
              <div className="mt-1 px-2" style={{ fontSize: '0.78rem', color: '#666', fontStyle: 'italic' }}>
                {risks.filter(r => r.type === 'Opportunity').map(r => r.description).join(', ') || 'No opportunities selected'}
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Approval Status table + Risk & Opportunities ── */}
        <div className="col-lg-6">

          {/* Approval Status – tabular */}
          <div className="mb-3">
            <SectionHeader label="Approval Status" />
            <div className="rounded overflow-hidden" style={{ border: '1px solid #dee2e6' }}>
              <table className="table table-sm mb-0" style={{ fontSize: '0.84rem' }}>
                <thead style={{ backgroundColor: '#f0f4ff' }}>
                  <tr>
                    <th className="py-2 px-3" style={{ fontWeight: '600', color: '#444', borderBottom: '1px solid #dee2e6' }}>
                      Department
                    </th>
                    <th className="py-2 px-3 text-end" style={{ fontWeight: '600', color: '#444', borderBottom: '1px solid #dee2e6' }}>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.approvals || []).length === 0 ? (
                    <tr>
                      <td colSpan={2} className="text-center text-muted py-3" style={{ fontSize: '0.82rem' }}>
                        Add departments in Impact Assessment, then use Submit for Review. Approvals are managed on the Approvals tab.
                      </td>
                    </tr>
                  ) : (
                    data.approvals.map((a, i) => (
                      <tr key={i} style={{ borderTop: i > 0 ? '1px solid #f0f0f0' : undefined }}>
                        <td className="py-2 px-3" style={{ color: '#333' }}>{a.department}:</td>
                        <td className="py-2 px-3 text-end">
                          <span
                            className="badge"
                            style={{
                              backgroundColor: statusBadgeColor(a.status),
                              color: '#fff',
                              fontSize: '0.76rem',
                              padding: '3px 12px',
                              borderRadius: '12px',
                            }}
                          >
                            {a.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>




        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────────────
   Tab 2 – Impact Assessment
   ──────────────────────────────────────────────────────────────────────────── */
const ImpactAssessmentTab = ({ data, onChange, departmentNames = [], processNames = [], assignableUsers = CR_ASSIGABLE_USERS_FALLBACK }) => {
  const assignableList = assignableUsers?.length ? assignableUsers : CR_ASSIGABLE_USERS_FALLBACK;
  const responsiblePersonOptions = assignableList.map(u => ({
    value: u.displayName,
    label: u.departmentName ? `${u.displayName} — ${u.departmentName}` : u.displayName,
    userId: u.userId || '',
  }));

  const [form, setForm] = useState({
    impactScope: '',
    impactLevel: 'Medium',
    affectedProcesses: '',
    affectedDepartments: Array.isArray(data?.impactAssessment?.affectedDepartments) 
      ? data.impactAssessment.affectedDepartments 
      : [{ department: '', responsiblePerson: '' }],
    customersImpacted: false,
    regulatoryImpact: false,
    regulatoryDetails: '',
    ...data?.impactAssessment,
  });

  const notifyChange = (updated) => {
    setForm(updated);
    onChange && onChange({ impactAssessment: updated });
  };

  const updateDeptRow = (idx, field, value) => {
    const updatedDepts = form.affectedDepartments.map((d, i) => 
      i === idx ? { ...d, [field]: value } : d
    );
    notifyChange({ ...form, affectedDepartments: updatedDepts });
  };

  const addDeptRow = () => {
    notifyChange({ 
      ...form, 
      affectedDepartments: [...form.affectedDepartments, { department: '', responsiblePerson: '' }] 
    });
  };

  const removeDeptRow = (idx) => {
    notifyChange({ 
      ...form, 
      affectedDepartments: form.affectedDepartments.filter((_, i) => i !== idx) 
    });
  };

  const handleChange = e => {
    const updated = {
      ...form,
      [e.target.name]:
        e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    };
    setForm(updated);
    onChange && onChange({ impactAssessment: updated });
  };

  const impactLevels = ['Low', 'Medium', 'High', 'Critical'];

  return (
    <div className="row g-3 mt-1">

      <div className="col-lg-6">
        <label className="form-label">Impact Scope</label>
        <textarea
          className="form-control"
          rows={3}
          name="impactScope"
          placeholder="Describe the scope of impact..."
          value={form.impactScope}
          onChange={handleChange}
        />
      </div>

      <div className="col-lg-6">
        <label className="form-label">Impact Level</label>
        <div className="d-flex gap-2 flex-wrap mb-2">
          {impactLevels.map(level => (
            <button
              key={level}
              type="button"
              className={`btn ${form.impactLevel === level ? 'btn-primary' : 'btn-outline-secondary'}`}
              style={{ fontSize: '0.82rem', padding: '4px 14px' }}
              onClick={() => {
                const updated = { ...form, impactLevel: level };
                setForm(updated);
                onChange && onChange({ impactAssessment: updated });
              }}
            >
              {level}
            </button>
          ))}
        </div>

        {/* Impact Toggles moved here */}
        <div className="d-flex gap-4 mt-2">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="customersImpacted"
              name="customersImpacted"
              checked={form.customersImpacted}
              onChange={handleChange}
            />
            <label className="form-check-label fw-semibold" htmlFor="customersImpacted" style={{ fontSize: '0.85rem' }}>
              Customers Impacted
            </label>
          </div>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="regulatoryImpact"
              name="regulatoryImpact"
              checked={form.regulatoryImpact}
              onChange={handleChange}
            />
            <label className="form-check-label fw-semibold" htmlFor="regulatoryImpact" style={{ fontSize: '0.85rem' }}>
              Regulatory Impact
            </label>
          </div>
        </div>
      </div>

      <div className={form.regulatoryImpact ? 'col-lg-6' : 'col-12'}>
        <label className="form-label">Affected Processes</label>
        <Select
          isMulti
          {...crModalSelectProps}
          menuPlacement="auto"
          classNamePrefix="react-select"
          options={processNames.map(p => ({ value: p, label: p }))}
          value={
            (form.affectedProcesses || '')
              .split(',')
              .filter(Boolean)
              .map(p => ({ value: p.trim(), label: p.trim() }))
          }
          onChange={selected => {
            const val = (selected || []).map(o => o.value).join(',');
            setForm(prev => {
              const updated = { ...prev, affectedProcesses: val };
              onChange && onChange({ impactAssessment: updated });
              return updated;
            });
          }}
          placeholder="Select affected processes..."
          styles={crModalSelectStyles({
            control: base => ({ ...base, minHeight: '38px', fontSize: '0.85rem', borderRadius: '8px' }),
          })}
        />
        <div className="mt-1 px-1" style={{ fontSize: '0.78rem', color: '#666', fontStyle: 'italic' }}>
          {form.affectedProcesses || 'No processes selected'}
        </div>
      </div>

      {form.regulatoryImpact && (
        <div className="col-lg-6">
          <label className="form-label mb-1" style={{ fontSize: '0.82rem' }} htmlFor="regulatoryDetailsImpact">
            Regulatory Details
          </label>
          <textarea
            id="regulatoryDetailsImpact"
            className="form-control form-control-sm"
            rows={4}
            name="regulatoryDetails"
            placeholder="Describe regulatory implications..."
            value={form.regulatoryDetails || ''}
            onChange={handleChange}
            style={{ minHeight: '5.5rem' }}
          />
        </div>
      )}

      <div className="col-12 mt-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label fw-bold mb-0">
            Affected Departments &amp; Responsible Persons <span className="text-danger">*</span>
          </label>
          <button type="button" className="btn btn-xs btn-outline-primary py-1 px-2" onClick={addDeptRow} style={{ fontSize: '0.75rem' }}>+ Add Row</button>
        </div>
        <div
          className="table-responsive rounded border"
          style={{
            borderColor: '#e2e8f0',
            /* ~thead + 3 data rows so the block is not a tiny scroll area */
            minHeight: '13.5rem',
          }}
        >
          <table className="table table-bordered table-sm align-middle mb-0" style={{ fontSize: '0.86rem' }}>
            <thead style={{ backgroundColor: '#f8fafc' }}>
              <tr>
                <th style={{ width: 40 }} className="text-center">#</th>
                <th style={{ width: '45%' }}>Affected Department</th>
                <th style={{ width: '45%' }}>Responsible Person</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {form.affectedDepartments.map((row, i) => (
                <tr key={i} style={{ height: '2.75rem' }}>
                  <td className="text-center text-muted">{i + 1}</td>
                  <td>
                    <Select
                      {...crModalSelectProps}
                      menuPlacement="auto"
                      options={departmentNames.map(d => ({ value: d, label: d }))}
                      value={row.department ? { value: row.department, label: row.department } : null}
                      onChange={v => updateDeptRow(i, 'department', v?.value)}
                      placeholder="Select Dept..."
                      classNamePrefix="react-select"
                      styles={crModalSelectStyles({
                        control: b => ({ ...b, border: 'none', minHeight: '32px', fontSize: '0.82rem' }),
                        indicatorSeparator: () => ({ display: 'none' }),
                      })}
                    />
                  </td>
                  <td>
                    <Select
                      {...crModalSelectProps}
                      menuPlacement="auto"
                      options={responsiblePersonOptions}
                      value={
                        row.responsiblePerson
                          ? (() => {
                            const opt = responsiblePersonOptions.find(o => o.value === row.responsiblePerson);
                            return opt || { value: row.responsiblePerson, label: row.responsiblePerson };
                          })()
                          : null
                      }
                      onChange={v => {
                        const updatedDepts = form.affectedDepartments.map((d, idx) =>
                          idx === i
                            ? {
                                ...d,
                                responsiblePerson: v?.value || '',
                                responsiblePersonUserId: v?.userId || '',
                              }
                            : d,
                        );
                        notifyChange({ ...form, affectedDepartments: updatedDepts });
                      }}
                      placeholder="Type to search users..."
                      isSearchable
                      filterOption={crAssignableUserFilter}
                      maxMenuHeight={300}
                      noOptionsMessage={({ inputValue }) =>
                        (inputValue ? `No users match "${inputValue}"` : 'Start typing to search')
                      }
                      classNamePrefix="react-select"
                      styles={crModalSelectStyles({
                        control: b => ({ ...b, border: 'none', minHeight: '32px', fontSize: '0.82rem' }),
                        indicatorSeparator: () => ({ display: 'none' }),
                      })}
                    />
                  </td>
                  <td className="text-center">
                    <button type="button" className="btn btn-link text-danger p-0" onClick={() => removeDeptRow(i)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────────────
   Tab 3 – Resources
   ──────────────────────────────────────────────────────────────────────────── */
const ResourcesTab = ({ data, onChange, categoryNames = [], assignableUsers = CR_ASSIGABLE_USERS_FALLBACK }) => {
  const assignableList = assignableUsers?.length ? assignableUsers : CR_ASSIGABLE_USERS_FALLBACK;
  const assignedToOptions = assignableList.map(u => ({
    value: u.displayName,
    label: u.departmentName ? `${u.displayName} — ${u.departmentName}` : u.displayName,
  }));
  const [resourceRows, setResourceRows] = useState(
    (data?.resourceDetails || []).length > 0
      ? data.resourceDetails
      : [{ id: 1, category: '', description: '', estimatedCost: '', assignedTo: '' }],
  );

  const addRow = () => {
    const updated = [
      ...resourceRows,
      {
        id: Date.now(),
        category: '',
        description: '',
        estimatedCost: '',
        assignedTo: '',
      },
    ];
    setResourceRows(updated);
    onChange && onChange({ resourceDetails: updated });
  };

  const removeRow = id => {
    const updated = resourceRows.filter(r => r.id !== id);
    setResourceRows(updated);
    onChange && onChange({ resourceDetails: updated });
  };

  const handleRowChange = (id, field, value) => {
    const updated = resourceRows.map(r =>
      r.id === id ? { ...r, [field]: value } : r,
    );
    setResourceRows(updated);
    onChange && onChange({ resourceDetails: updated });
  };

  return (
    <div className="mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <label className="form-label fw-semibold text-primary mb-0">Resource Plan</label>
        <button className="btn btn-sm btn-outline-primary" type="button" onClick={addRow}>
          + Add Resource
        </button>
      </div>
      <div className="table-responsive">
        <table className="table table-bordered table-hover align-middle" style={{ fontSize: '0.88rem' }}>
          <thead style={{ backgroundColor: '#f0f4ff' }}>
            <tr>
              <th>Category</th>
              <th>Description</th>
              <th>Estimated Cost</th>
              <th>Assigned To</th>
              <th style={{ width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {resourceRows.map(row => (
              <tr key={row.id}>
                <td>
                  <select
                    className="form-select form-select-sm"
                    value={row.category}
                    onChange={e => handleRowChange(row.id, 'category', e.target.value)}
                  >
                    <option value="">Select...</option>
                    {categoryNames.map(o => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Description"
                    value={row.description}
                    onChange={e => handleRowChange(row.id, 'description', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="$ 0.00"
                    value={row.estimatedCost}
                    onChange={e => handleRowChange(row.id, 'estimatedCost', e.target.value)}
                  />
                </td>
                <td>
                  <Select
                    {...crModalSelectProps}
                    menuPlacement="auto"
                    options={assignedToOptions}
                    value={
                      row.assignedTo
                        ? (
                            assignedToOptions.find(o => o.value === row.assignedTo)
                            || { value: row.assignedTo, label: row.assignedTo }
                          )
                        : null
                    }
                    onChange={opt => handleRowChange(row.id, 'assignedTo', opt?.value ?? '')}
                    placeholder="Type to search users..."
                    isSearchable
                    isClearable
                    filterOption={crAssignableUserFilter}
                    maxMenuHeight={300}
                    noOptionsMessage={({ inputValue }) =>
                      (inputValue ? `No users match "${inputValue}"` : 'Start typing to search')
                    }
                    classNamePrefix="react-select"
                    styles={crModalSelectStyles({
                      control: base => ({ ...base, minHeight: '34px', fontSize: '0.84rem' }),
                    })}
                  />
                </td>
                <td className="text-center">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => removeRow(row.id)}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────────────
   Tab 4 – Approvals
   ──────────────────────────────────────────────────────────────────────────── */
const ApprovalsTab = ({ data, onChange }) => {
  const crStatus = data?.status || 'Pending';
  const inReview = crStatus === 'In Review';

  // Sync with Impact Assessment departments
  const configDepts = data?.impactAssessment?.affectedDepartments || [];
  
  const [approvals, setApprovals] = useState(() => {
    const existing = data.approvals || [];
    return configDepts.map(cd => {
      const match = existing.find(
        ex => ex.department === cd.department && ex.responsiblePerson === cd.responsiblePerson,
      );
      return match
        ? {
            ...match,
            responsiblePersonUserId:
              cd.responsiblePersonUserId || match.responsiblePersonUserId || '',
          }
        : { ...cd, status: 'Pending', comments: '' };
    });
  });

  // Re-sync if Impact Assessment changes (includes responsiblePersonUserId / Entra oid).
  // Must notify parent so Submit for Review includes the oid in the save payload.
  React.useEffect(() => {
    const next = configDepts.map(cd => {
      const match = approvals.find(
        p =>
          p.department === cd.department && p.responsiblePerson === cd.responsiblePerson,
      );
      return match
        ? {
            ...match,
            responsiblePersonUserId:
              cd.responsiblePersonUserId || match.responsiblePersonUserId || '',
          }
        : { ...cd, status: 'Pending', comments: '' };
    });

    const same =
      next.length === approvals.length &&
      next.every(
        (row, i) =>
          row.department === approvals[i].department &&
          row.responsiblePerson === approvals[i].responsiblePerson &&
          (row.responsiblePersonUserId || '') === (approvals[i].responsiblePersonUserId || '') &&
          row.status === approvals[i].status,
      );
    if (same) return;

    setApprovals(next);
    onChange && onChange({ approvals: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync from Impact Assessment only
  }, [JSON.stringify(configDepts)]);

  const statusOptions = ['Pending', 'Approved', 'Rejected', 'In Review'];
  const statusColors = {
    Approved: '#28a745',
    Pending: '#ffc107',
    Rejected: '#dc3545',
    'In Review': '#17a2b8',
  };

  const handleChange = (i, field, value) => {
    const updated = approvals.map((a, idx) =>
      idx === i ? { ...a, [field]: value } : a,
    );
    setApprovals(updated);
    onChange && onChange({ approvals: updated });
  };

  return (
    <div className="mt-2">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="form-label fw-semibold text-primary mb-0">Approval Workflow (Based on Impact Assessment)</label>
      </div>
      {!inReview && (
        <p className="text-muted small mb-3" style={{ fontSize: '0.8rem' }}>
          Use <strong>Submit for Review</strong> to set this change request to <strong>In Review</strong>. After that, each
          responsible person can approve their row.
        </p>
      )}
      {inReview && (
        <p className="text-info small mb-3" style={{ fontSize: '0.8rem' }}>
          This request is <strong>In Review</strong>. You can approve only rows where you are the responsible person
          (matched to your signed-in name).
        </p>
      )}

      {/* Summary badges */}
      <div className="d-flex gap-3 mb-3 flex-wrap">
        {Object.entries(statusColors).map(([status, color]) => {
          const count = approvals.filter(a => a.status === status).length;
          return (
            <span
              key={status}
              className="badge"
              style={{ backgroundColor: color, color: '#fff', fontSize: '0.8rem', padding: '6px 12px' }}
            >
              {count} {status}
            </span>
          );
        })}
      </div>

      <div className="table-responsive">
        <table className="table table-bordered align-middle mb-0" style={{ fontSize: '0.86rem' }}>
          <thead style={{ backgroundColor: '#f0f4ff' }}>
            <tr>
              <th style={{ width: 40 }} className="text-center">#</th>
              <th style={{ width: 220 }}>Affected Department</th>
              <th style={{ width: 200 }}>Responsible Person</th>
              <th style={{ width: 180 }}>Status</th>
              <th style={{ width: 110 }} className="text-center">Action</th>
              <th>Comments</th>
            </tr>
          </thead>
          <tbody>
            {approvals.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-muted py-3">
                  No departments selected in Impact Assessment.
                </td>
              </tr>
            ) : (
              approvals.map((a, i) => {
                const canActAsApprover = inReview && responsiblePersonIsCurrentUser(a.responsiblePerson);
                const approvalLocked = !inReview;
                return (
                  <tr key={i}>
                    <td className="text-center text-muted">{i + 1}</td>
                    <td className="fw-bold" style={{ color: '#2d3748' }}>{a.department}</td>
                    <td style={{ color: '#4a5568' }}>{a.responsiblePerson || <em className="text-muted">Not Assigned</em>}</td>
                    <td>
                      <select
                        className="form-select form-select-sm fw-bold"
                        value={a.status}
                        onChange={e => handleChange(i, 'status', e.target.value)}
                        disabled={approvalLocked || !canActAsApprover}
                        title={
                          approvalLocked
                            ? 'Submit for Review to enable approvals'
                            : (canActAsApprover ? '' : 'Only the responsible person can update this approval')
                        }
                        style={{
                          color: statusColors[a.status] || '#333',
                          fontSize: '0.82rem',
                          height: '34px',
                          borderRadius: '6px',
                        }}
                      >
                        {statusOptions.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="text-center">
                      {inReview && canActAsApprover && a.status !== 'Approved' && (
                        <button
                          type="button"
                          className="btn btn-sm btn-success"
                          style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                          onClick={() => handleChange(i, 'status', 'Approved')}
                        >
                          Approve
                        </button>
                      )}
                      {inReview && !canActAsApprover && (
                        <span className="text-muted" style={{ fontSize: '0.72rem' }}>—</span>
                      )}
                      {!inReview && <span className="text-muted" style={{ fontSize: '0.72rem' }}>—</span>}
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Comments..."
                        value={a.comments || ''}
                        onChange={e => handleChange(i, 'comments', e.target.value)}
                        disabled={approvalLocked || !canActAsApprover}
                        title={
                          approvalLocked
                            ? 'Submit for Review first'
                            : (canActAsApprover ? '' : 'Only the responsible person can comment')
                        }
                        style={{ height: '34px', fontSize: '0.82rem', borderRadius: '6px' }}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────────────
   Main modal component
   ──────────────────────────────────────────────────────────────────────────── */
const emptyChangeRequestMasters = () => ({
  departments: [],
  resourceCategories: [],
  risks: [],
  opportunities: [],
  affectedProcesses: [],
});

const defaultEmptyResourceDetails = () => [
  { id: 1, category: '', description: '', estimatedCost: '', assignedTo: '' },
];

/** Impact Assessment tab state only (not the full modal object). */
function buildImpactSliceFromModal(modal) {
  const ia = modal?.impactAssessment || {};
  return {
    impactScope: ia.impactScope ?? '',
    impactLevel: ia.impactLevel ?? 'Medium',
    affectedProcesses: typeof ia.affectedProcesses === 'string' ? ia.affectedProcesses : '',
    affectedDepartments:
      Array.isArray(ia.affectedDepartments) && ia.affectedDepartments.length > 0
        ? ia.affectedDepartments
        : [{ department: '', responsiblePerson: '' }],
    customersImpacted: !!ia.customersImpacted,
    regulatoryImpact: !!ia.regulatoryImpact,
    regulatoryDetails: ia.regulatoryDetails ?? '',
  };
}

const savingButtonContent = (
  <>
    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
    Saving…
  </>
);

const ChangeRequestAddEdit = ({ showModal, modalData, onCloseModal }) => {
  const dispatch = useDispatch();
  const saveLoading = useSelector(selectChangeRequestSaveLoading);
  const isEditMode = Boolean(modalData?.id);
  const crRecordKey = modalData?.changeRequestId ?? modalData?.id ?? 'new';

  const [detailsData, setDetailsData] = useState(() => modalData || {});
  const [impactData, setImpactData] = useState(() => buildImpactSliceFromModal(modalData));
  const [resourceData, setResourceData] = useState(() => ({
    resourceDetails:
      modalData?.resourceDetails?.length > 0
        ? modalData.resourceDetails
        : defaultEmptyResourceDetails(),
  }));
  const [approvalsData, setApprovalsData] = useState(() => ({
    approvals: modalData?.approvals || [],
  }));
  const [masters, setMasters] = useState(emptyChangeRequestMasters);
  const [assignableUsers, setAssignableUsers] = useState(CR_ASSIGABLE_USERS_FALLBACK);

  useEffect(() => {
    if (!showModal) return undefined;
    setDetailsData(modalData || {});
    setImpactData(buildImpactSliceFromModal(modalData));
    setResourceData({
      resourceDetails:
        modalData?.resourceDetails?.length > 0
          ? modalData.resourceDetails
          : defaultEmptyResourceDetails(),
    });
    setApprovalsData({ approvals: modalData?.approvals || [] });
  }, [showModal, crRecordKey]);

  useEffect(() => {
    if (!showModal) return undefined;
    let cancelled = false;
    (async () => {
      const [mastersRes, usersRes] = await Promise.allSettled([
        ChangeRequestService.fetchAllMasters(),
        ChangeRequestService.getDummyAssignableUsers(),
      ]);
      if (cancelled) return;

      if (mastersRes.status === 'fulfilled') {
        const m = mastersRes.value;
        setMasters({
          departments: (m.departments || [])
            .map(d => d.departmentName ?? d.DepartmentName)
            .filter(Boolean),
          resourceCategories: (m.resourceCategories || [])
            .map(c => c.categoryName ?? c.CategoryName)
            .filter(Boolean),
          risks: (m.risks || [])
            .map(r => r.riskName ?? r.RiskName)
            .filter(Boolean),
          opportunities: (m.opportunities || [])
            .map(o => o.opportunityName ?? o.OpportunityName)
            .filter(Boolean),
          affectedProcesses: (m.affectedProcesses || [])
            .map(p => p.processName ?? p.ProcessName)
            .filter(Boolean),
        });
      } else {
        console.error('Change request master data:', mastersRes.reason);
        setMasters(emptyChangeRequestMasters());
        dispatch(showToast({ message: 'Could not load change request master data.', type: 'danger' }));
      }

      if (usersRes.status === 'fulfilled' && usersRes.value?.length > 0) {
        setAssignableUsers(usersRes.value);
      } else {
        if (usersRes.status === 'rejected') {
          console.error('Change request assignable users:', usersRes.reason);
        }
        setAssignableUsers(CR_ASSIGABLE_USERS_FALLBACK);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showModal, dispatch]);

  const handleClose = () => {
    if (saveLoading) return;
    onCloseModal();
  };

  /* ── Common Change Management Steps stepper ── */
  const CRStepper = () => (
    <div
      className="d-flex align-items-center gap-1"
      style={{
        background: '#f8f9fa',
        border: '1px solid #e8eaf0',
        borderRadius: '8px',
        padding: '8px 16px',
      }}
    >
      {[
        { num: 1, label: 'Identify &\nPropose' },
        { num: 2, label: 'Evaluate\nImpact' },
        { num: 3, label: 'Plan &\nResource' },
        { num: 4, label: 'Communicate' },
        { num: 5, label: 'Verify &\nMonitor' },
      ].map((step, i, arr) => (
        <React.Fragment key={step.num}>
          <div className="text-center" style={{ minWidth: 56 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: '#1a56db',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.9rem',
                fontWeight: '700',
                margin: '0 auto 3px',
              }}
            >
              {step.num}
            </div>
            <div style={{ fontSize: '0.62rem', color: '#555', whiteSpace: 'pre-line', lineHeight: '1.2' }}>
              {step.label}
            </div>
          </div>
          {i < arr.length - 1 && (
            <div style={{ height: 2, width: 18, backgroundColor: '#bbb', marginBottom: 18, flexShrink: 0 }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const body = () => (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 240,
      }}
    >
      {saveLoading && (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(255,255,255,0.82)',
            zIndex: 12,
            borderRadius: 6,
          }}
          aria-busy="true"
          aria-live="polite"
        >
          <div className="text-center">
            <div className="spinner-border text-primary" role="status" style={{ width: '2.5rem', height: '2.5rem' }} />
            <div className="mt-2 small text-muted fw-semibold">Saving change request…</div>
          </div>
        </div>
      )}

      {/* ── Common header: CR info (left) + Stepper (right) ── */}
      <div
        className="d-flex align-items-start justify-content-between pb-2 mb-2"
        style={{ borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}
      >
        <div>
          {isEditMode ? (
            <>
              <div style={{ color: '#1a56db', fontWeight: '600', fontSize: '0.88rem' }}>
                Change Request: {modalData.crNumber}
              </div>
              <h6 className="mb-0 mt-1 fw-bold">{modalData.title}</h6>
            </>
          ) : (
            <div className="row g-2" style={{ maxWidth: 550 }}>
              <div className="col-6">
                <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>Change Title <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Enter change title"
                  value={detailsData.title || ''}
                  onChange={e => setDetailsData(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>
              <div className="col-3">
                <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>Status</label>
                <select
                  className="form-select form-select-sm"
                  value={detailsData.status || 'Pending'}
                  onChange={e => setDetailsData(prev => ({ ...prev, status: e.target.value }))}
                >
                  {['Pending', 'In Review', 'Approved', 'Rejected', 'Cancelled'].map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="col-3">
                <label className="form-label mb-1" style={{ fontSize: '0.8rem' }}>Initiator</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Name"
                  value={detailsData.initiator || ''}
                  onChange={e => setDetailsData(prev => ({ ...prev, initiator: e.target.value }))}
                />
              </div>
            </div>
          )}
        </div>
        {/* Stepper – always visible, right side */}
        <div className="ms-3" style={{ flexShrink: 0 }}>
          <CRStepper />
        </div>
      </div>

      {/* ── Scrollable tab content ── */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <Tabs>
          <Tab title="Details">
            <DetailsTab
              key={`cr-details-${crRecordKey}`}
              data={detailsData}
              masterRisks={masters.risks}
              masterOpportunities={masters.opportunities}
              resourceDetails={resourceData.resourceDetails}
              onChange={d => setDetailsData(prev => ({ ...prev, ...d }))}
            />
          </Tab>
          <Tab title="Impact Assessment">
            <ImpactAssessmentTab
              key={`cr-impact-${crRecordKey}`}
              data={{ ...modalData, impactAssessment: impactData }}
              departmentNames={masters.departments}
              processNames={masters.affectedProcesses}
              assignableUsers={assignableUsers}
              onChange={d =>
                setImpactData(prev => ({ ...prev, ...(d.impactAssessment || {}) }))
              }
            />
          </Tab>
          <Tab title="Resources">
            <ResourcesTab
              key={`cr-resources-${crRecordKey}`}
              data={{ ...modalData, resourceDetails: resourceData.resourceDetails }}
              categoryNames={masters.resourceCategories}
              assignableUsers={assignableUsers}
              onChange={d => setResourceData(prev => ({ ...prev, ...d }))}
            />
          </Tab>
          <Tab title="Approvals">
            <ApprovalsTab
              key={`cr-approvals-${crRecordKey}`}
              data={{
                ...modalData,
                status: detailsData.status ?? modalData?.status,
                approvals: approvalsData.approvals,
                impactAssessment: impactData,
              }}
              onChange={d => setApprovalsData(prev => ({ ...prev, ...d }))}
            />
          </Tab>
        </Tabs>
      </div>
    </div>
  );

  const footer = () => {
    const runSave = (submitForReview = false, statusOverride = null) => {
      if (saveLoading) return;
      const cancelling = statusOverride === 'Cancelled';
      if (!cancelling && !detailsData.title && !modalData?.title) {
        dispatch(showToast({ message: 'Please enter a Change Title', type: 'danger' }));
        return;
      }
      const impactDepts = impactData?.affectedDepartments || [];
      if (!cancelling) {
        const completeDeptRows = impactDepts.filter(
          d => d && String(d.department || '').trim() && String(d.responsiblePerson || '').trim(),
        );
        if (completeDeptRows.length === 0) {
          dispatch(showToast({
            message: 'Please add at least one Affected Department and Responsible Person',
            type: 'danger',
          }));
          return;
        }
      }
      const nextStatus = cancelling
        ? 'Cancelled'
        : submitForReview
          ? 'In Review'
          : (detailsData.status ?? modalData?.status ?? 'Pending');

      // Approvals may be empty if the Approvals tab never pushed to parent.
      // Build them from Impact Assessment so Entra oids are always included on create.
      let approvals = approvalsData.approvals || modalData?.approvals || [];
      if ((!approvals || approvals.length === 0) && impactDepts.length > 0) {
        approvals = impactDepts
          .filter(d => d && (d.department || d.responsiblePerson))
          .map(d => ({
            department: d.department || '',
            responsiblePerson: d.responsiblePerson || '',
            responsiblePersonUserId: d.responsiblePersonUserId || '',
            status: 'Pending',
            comments: '',
          }));
      } else if (approvals?.length > 0 && impactDepts.length > 0) {
        // Fill missing oids from Impact Assessment rows.
        approvals = approvals.map(a => {
          if (a.responsiblePersonUserId) return a;
          const match = impactDepts.find(
            d =>
              d.department === a.department && d.responsiblePerson === a.responsiblePerson,
          );
          return match?.responsiblePersonUserId
            ? { ...a, responsiblePersonUserId: match.responsiblePersonUserId }
            : a;
        });
      }

      const payload = {
        ...(modalData || {}),
        ...detailsData,
        ...resourceData,
        ...approvalsData,
        approvals,
        impactAssessment: impactData,
        status: nextStatus,
        approvalsCount: (approvals || []).filter(a => a.status === 'Approved').length,
        approvalsTotal: (approvals || []).length,
        backgroundColor:
          nextStatus === 'Approved' ? '#d4edda' :
            nextStatus === 'Pending' ? '#f8d7da' :
              nextStatus === 'In Review' ? '#d1ecf1' :
                nextStatus === 'Cancelled' ? '#e2e3e5' : '#fff3cd',
        textColor:
          nextStatus === 'Approved' ? '#155724' :
            nextStatus === 'Pending' ? '#721c24' :
              nextStatus === 'In Review' ? '#0c5460' :
                nextStatus === 'Cancelled' ? '#383d41' : '#856404',
      };

      if (isEditMode) {
        dispatch(editChangeRequest(payload));
      } else {
        dispatch(addChangeRequest(payload));
      }
    };

    const onSave = () => runSave(false);
    const onSubmitForReview = () => runSave(true);
    const onCancelRequest = () => {
      if (saveLoading) return;
      if (!isEditMode) {
        handleClose();
        return;
      }
      runSave(false, 'Cancelled');
    };

    return (
      <div className="row">
        <div className="col-lg-6">
          <button
            type="button"
            className="btn btn-info me-2"
            onClick={onSubmitForReview}
            disabled={
              saveLoading
              || (detailsData.status ?? modalData?.status) === 'In Review'
              || (detailsData.status ?? modalData?.status) === 'Cancelled'
            }
            title={
              (detailsData.status ?? modalData?.status) === 'In Review'
                ? 'Already in review'
                : (detailsData.status ?? modalData?.status) === 'Cancelled'
                  ? 'Request is cancelled'
                  : 'Save and set status to In Review'
            }
          >
            Submit for Review
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={onCancelRequest}
            disabled={
              saveLoading
              || (isEditMode && (detailsData.status ?? modalData?.status) === 'Cancelled')
            }
            title={
              isEditMode
                ? 'Save and set status to Cancelled'
                : 'Close without saving'
            }
          >
            Cancel Request
          </button>
        </div>
        <div className="col-lg-6">
          <FooterButton
            cancelText="Close"
            saveText={
              saveLoading
                ? savingButtonContent
                : (isEditMode ? 'Save Changes' : 'Create Change Request')
            }
            onSave={onSave}
            onCancel={handleClose}
            disableSave={saveLoading}
            disableCancel={saveLoading}
          />
        </div>
      </div>
    );
  };

  return (
    <Modal
      id="change-request-modal"
      show={showModal}
      size="xl"
      dialogClassName="modal-fullscreen-lg-down modal-90w cr-fit-modal"
      title={isEditMode ? `Edit Change Request: ${modalData?.crNumber}` : 'New Change Request'}
      body={body}
      footer={footer}
      handleClose={handleClose}
      backdrop={saveLoading ? 'static' : true}
      keyboard={!saveLoading}
      style={{ maxHeight: '95vh' }}
    />
  );
};

ChangeRequestAddEdit.propTypes = {
  showModal: PropTypes.bool,
  modalData: PropTypes.object,
  onCloseModal: PropTypes.func,
};

export default AppModal(ChangeRequestAddEdit);
